import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider, UiStringsProvider } from '@pmai/ui';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router';
import { ApiError, setUnauthenticatedHandler } from '../shared/api/client';
import { ME_KEY } from '../shared/auth/session';
import { createRouter } from './router';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // API errors are answers, not glitches — retry only network-level failures.
      retry: (count, error) => (!(error instanceof ApiError) || error.code === 'NETWORK') && count < 2,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
  mutationCache: new MutationCache(),
});

// FR-AUTH-005: the session ended while working → the app shell sees "signed out" and redirects.
setUnauthenticatedHandler(() => queryClient.setQueryData(ME_KEY, null));

const router = createRouter();

/** Fixed component words for @pmai/ui, from the active language. */
function UiStrings({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const value = useMemo(
    () => ({
      cancel: t('common.cancel'),
      close: t('common.close'),
      save: t('common.save'),
      loading: t('common.loading'),
      moreSections: t('common.more'),
      previousPage: t('common.previous'),
      nextPage: t('common.next'),
      pageRange: (from: string, to: string, total: string) => t('common.pageRange', { from, to, total }),
      typeToConfirm: (word: string) => t('common.typeToConfirm', { word }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute on language change
    [t, i18n.language],
  );
  return <UiStringsProvider value={value}>{children}</UiStringsProvider>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <UiStrings>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </UiStrings>
    </QueryClientProvider>
  );
}
